<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import i18n from '@/plugins/i18n';

import { RouteNames } from '@/data/routes';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { JobTitles, OWNER_ROLES } from '@clearbanc/data-common-types';
import { useToast } from '@/composables/useToast';

import ConfirmDialog from 'primevue/confirmdialog';
import DSButton from 'primevue/button';
import UserListItem from '@/pages/users/components/UserListItem.vue';
import UserChangeSignatoryModal from '@/pages/users/components/UserChangeSignatoryModal.vue';
import { UserResponseDto } from '@clearbanc/baus-sdk';

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

// ---------
// Variables
// ---------

const eligibleOwners = computed<UserResponseDto[]>(
  () => store.getters.eligibleOwners,
);

const modalSignatoryOwner = ref<UserResponseDto>();

// ------------
// Misc Methods
// ------------

function addNewOwner() {
  analytics.track(SEGMENT_EVENTS.if_secondary_profile_added);

  router.push({
    name: RouteNames.OWNERS.NEW,
  });
}

function showSignatoryModal(owner: UserResponseDto) {
  modalSignatoryOwner.value = owner;
}

function hideSignatoryModal() {
  modalSignatoryOwner.value = undefined;
}

async function removeBusinessOwner(owner: UserResponseDto) {
  if (owner.jobRoles.includes(OWNER_ROLES.SIGNATORY)) {
    toast.error(
      i18n.t('data.errorMessages.cannotRemoveSignatoryOwner', {
        fullName: owner.fullName,
      }),
    );
    return;
  }
  await store.dispatchApiAction('UPDATE_USER', {
    id: owner.id,
    jobTitle: JobTitles.OTHER, // This is to ensure job title is set and the user profile remains complete
    jobRoles: [],
  });
  if (store.getters.requestStatus('UPDATE_USER').isSuccess) {
    analytics.track(SEGMENT_EVENTS.if_business_owner_removed, {
      user_id: owner.id,
    });
    toast.success(
      `Successfully removed ${owner.fullName} as a business owner.`,
    );
  } else {
    toast.error(
      `Failed to remove ${owner.fullName} as a business owner. Please try again.`,
    );
  }
}

function toggleRemoveOwnerModal(owner: UserResponseDto) {
  confirm.require({
    message: `Are you sure you want to remove ${owner.fullName} as a business owner? This action will remove their ownership permissions, but they will remain on the team.`,
    header: 'Remove business owner',
    accept: async () => {
      await removeBusinessOwner(owner);
    },
    acceptLabel: 'Remove',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-primary',
    rejectClass: 'p-button-outlined',
  });
}

onMounted(() => {
  store.dispatchApiAction('GET_USERS_FOR_BUSINESS');
});
</script>

<template>
  <div>
    <UserListItem
      v-for="owner in eligibleOwners"
      :key="owner.id"
      :owner="owner"
      @setAsSignatory="showSignatoryModal(owner)"
      @removeOwner="toggleRemoveOwnerModal(owner)"
    />
    <div :class="$style.actions">
      <DSButton
        label="Add New Owner or Director"
        class="p-button-outlined"
        :class="$style.actions__button"
        @click="addNewOwner"
      />
    </div>
  </div>

  <UserChangeSignatoryModal
    :visible="!!modalSignatoryOwner"
    :owner="modalSignatoryOwner"
    @close="hideSignatoryModal"
  />
  <ConfirmDialog :style="{ width: '600px' }"></ConfirmDialog>
</template>

<style lang="less" module>
.actions {
  margin-top: 24px;
}

.actions__button {
  width: 100%;
}
</style>
